/**
 * Payment recording dialog. Used from the party-ledger screen.
 *
 * Two modes:
 *   - two-party (default): payer + payee + amount + method + note
 *   - three-party `via_self`: explicit checkbox + visible warning panel
 *     that describes what will hit each ledger, before submit
 *
 * Attachments are uploaded after the payment is created (so the backend
 * can resolve ledger-entry ids and attach to the right side). For now we
 * attach the file to the PAYER-side entry as the default — users can
 * re-attach to the payee side from the party ledger screen if they need.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { LedgerEntry, Party, Payment, PaymentMethod } from '../api';
import {
  Button,
  Card,
  Checkbox,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';
import { fileToBase64 } from '../lib/file';
import { inrToKinrString, inrToKinrPreview } from '../money/inr';

const METHODS: PaymentMethod[] = ['cash', 'bank_transfer', 'upi', 'cheque', 'other'];

// localStorage key for remembering the user's last-used payment options
// across dialog openings. Amounts, notes, and effective date are NEVER
// persisted (they always start fresh). Payer/payee are persisted only when
// the caller hasn't pre-selected them via props.
const PREFS_KEY = 'paymentDialog.prefs.v1';

interface PaymentDialogPrefs {
  method?: PaymentMethod;
  viaSelf?: boolean;
  lastPayerPartyId?: string;
  lastPayeePartyId?: string;
}

function readPrefs(): PaymentDialogPrefs {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writePrefs(p: PaymentDialogPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // ignore quota errors
  }
}

export interface PaymentDialogProps {
  /** If provided, the payee dropdown is pre-selected and usually locked. */
  preselectPayeeId?: number | null;
  /** If provided, the payer dropdown is pre-selected. */
  preselectPayerId?: number | null;
  onClose: () => void;
  onCreated: (payment: Payment) => void;
}

export function PaymentDialog({
  preselectPayeeId,
  preselectPayerId,
  onClose,
  onCreated,
}: PaymentDialogProps) {
  const qc = useQueryClient();
  const parties = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });

  // Read remembered preferences once at mount. Caller-supplied preselects
  // always win over remembered values — prefs only fill the gaps.
  const prefs = useMemo(() => readPrefs(), []);

  const [payerPartyId, setPayerPartyId] = useState(
    preselectPayerId
      ? String(preselectPayerId)
      : prefs.lastPayerPartyId ?? '',
  );
  const [payeePartyId, setPayeePartyId] = useState(
    preselectPayeeId
      ? String(preselectPayeeId)
      : prefs.lastPayeePartyId ?? '',
  );
  const [effectiveDate, setEffectiveDate] = useState(() => todayIso());
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>(
    prefs.method ?? 'bank_transfer',
  );
  const [viaSelf, setViaSelf] = useState<boolean>(prefs.viaSelf ?? false);
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Once the payment is created we lock it here to prevent accidental
  // double-submission if the user retries after an attachment error.
  const [createdPayment, setCreatedPayment] = useState<Payment | null>(null);

  const partiesById = useMemo(
    () => new Map((parties.data ?? []).map((p) => [p.id, p])),
    [parties.data],
  );
  const payer = payerPartyId ? partiesById.get(Number(payerPartyId)) : undefined;
  const payee = payeePartyId ? partiesById.get(Number(payeePartyId)) : undefined;
  const sameParty = payerPartyId !== '' && payerPartyId === payeePartyId;

  const createMut = useMutation({
    mutationFn: async () => {
      // Guard: if the payment was already created (e.g. user retried after an
      // attachment error) skip straight to re-uploading attachments.
      const payment: Payment = createdPayment ?? await (async () => {
        if (!payerPartyId || !payeePartyId) throw new Error('Pick both payer and payee.');
        if (sameParty) throw new Error('Payer and payee must differ.');
        if (!amount.trim()) throw new Error('Amount is required.');
        if (!effectiveDate) throw new Error('Effective date is required.');
        const kinrAmount = inrToKinrString(amount);
        if (kinrAmount === null) throw new Error('Amount is not a valid number.');
        const p = await api.post<Payment>('/api/payments', {
          effectiveDate,
          amount: kinrAmount,
          method,
          payerPartyId: Number(payerPartyId),
          payeePartyId: Number(payeePartyId),
          viaSelf,
          note: note.trim() || null,
        });
        setCreatedPayment(p);
        return p;
      })();

      // Attach receipts to the payer-side ledger entry.
      // Use the dedicated /api/payments/:id/entries endpoint — reliable,
      // no date-range guessing required.
      if (files.length > 0) {
        const entries = await api.get<LedgerEntry[]>(`/api/payments/${payment.id}/entries`);
        const entry = entries.find((e) => e.partyId === payment.payerPartyId);
        if (!entry) {
          throw new Error(
            `Payment #${payment.id} was recorded but receipts couldn't be attached ` +
            `(payer ledger entry not found). Close this dialog and add them from the ledger page.`,
          );
        }
        for (const f of files) {
          const base64 = await fileToBase64(f);
          await api.post(`/api/ledger-entries/${entry.id}/attachments`, {
            base64,
            originalFilename: f.name,
            mimeType: f.type || 'application/octet-stream',
          });
        }
      }
      return payment;
    },
    onSuccess: (p) => {
      // Remember the options the user just used so the next dialog
      // opens with the same defaults (payer/payee only if they
      // weren't preselected — preselect implies contextual, not
      // a user preference).
      writePrefs({
        method,
        viaSelf,
        lastPayerPartyId: preselectPayerId ? undefined : payerPartyId || undefined,
        lastPayeePartyId: preselectPayeeId ? undefined : payeePartyId || undefined,
      });
      void qc.invalidateQueries();
      onCreated(p);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-center bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="flex min-h-[60vh] w-full max-w-3xl flex-col overflow-hidden rounded-md border border-border bg-surface shadow-card">
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <h2 className="text-small font-semibold text-ink">Record payment</h2>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </header>

        <form
          className="flex-1 overflow-y-auto p-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            createMut.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Payer"
              value={payerPartyId}
              onChange={setPayerPartyId}
              options={(parties.data ?? []).map((p) => ({
                value: String(p.id),
                label: `${p.displayName} (${p.role})`,
              }))}
              placeholder="Choose…"
            />
            <Select
              label="Payee"
              value={payeePartyId}
              onChange={setPayeePartyId}
              options={(parties.data ?? []).map((p) => ({
                value: String(p.id),
                label: `${p.displayName} (${p.role})`,
              }))}
              placeholder="Choose…"
            />
            <TextInput
              label="Effective date"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
            <div className="flex flex-col">
              <TextInput
                label="Amount (₹ INR)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 50000"
                inputMode="decimal"
                hint="Positive. Enter amount in rupees — stored internally as K-INR."
              />
              {amount.trim().length > 0 && (
                <p className={`mt-1 text-caption ${
                  inrToKinrPreview(amount) ? 'text-pos' : 'text-neg'
                }`}>
                  {inrToKinrPreview(amount) ?? 'Not a valid amount'}
                  {inrToKinrPreview(amount) && (
                    <span className="ml-1 text-ink-muted">(stored value)</span>
                  )}
                </p>
              )}
            </div>
            <Select
              label="Method"
              value={method}
              onChange={(v) => setMethod(v as PaymentMethod)}
              options={METHODS.map((m) => ({ value: m, label: m }))}
            />
            <div className="col-span-full">
              <TextArea
                label="Note (appears in the ledger description)"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. C0F999 VICKY"
              />
            </div>
          </div>

          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
            <Checkbox
              label="This is a three-party pass-through (paid directly on my behalf)"
              hint="Check this if the payer paid the payee directly and the transaction nets through me without hitting my self ledger."
              checked={viaSelf}
              onChange={setViaSelf}
            />
            {viaSelf && (
              <div className="mt-3 text-caption text-amber-700">
                <div className="mb-2">Preview of what will happen:</div>
                <ul className="ml-4 list-disc space-y-1">
                  <li>
                    <strong>{payer?.displayName ?? '(payer)'}</strong>&apos;s ledger: Cr {inrToKinrString(amount) ?? '0'} K-INR
                  </li>
                  <li>
                    <strong>{payee?.displayName ?? '(payee)'}</strong>&apos;s ledger: Dr {inrToKinrString(amount) ?? '0'} K-INR
                  </li>
                  <li>My self ledger: no entry (net zero).</li>
                </ul>
              </div>
            )}
            {!viaSelf && payer && payee && (
              <div className="mt-3 text-caption text-ink-muted">
                Preview:
                <span className="ml-1">
                  {payer.displayName}: Cr {inrToKinrString(amount) ?? '0'} · {payee.displayName}: Dr {inrToKinrString(amount) ?? '0'} K-INR
                </span>
              </div>
            )}
          </div>

          <div className="mt-4">
            <label className="block text-caption font-medium text-ink">
              Attach receipt(s) (optional)
            </label>
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              className="mt-1 block w-full text-caption text-ink-muted file:mr-3 file:cursor-pointer file:rounded-md file:border file:border-border file:bg-surface-raised file:px-3 file:py-1 file:text-small file:text-ink"
              onChange={(e) => {
                if (!e.target.files) return;
                setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
              }}
            />
            {files.length > 0 && (
              <ul className="mt-2 space-y-1 text-caption text-ink-muted">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between">
                    <span>
                      <Pill>{(f.size / 1024).toFixed(0)} kB</Pill>
                      <span className="ml-2">{f.name}</span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <div className="mt-3"><ErrorBanner>{error}</ErrorBanner></div>}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? (
                <Spinner />
              ) : createdPayment ? (
                'Retry attaching receipts'
              ) : (
                'Record payment'
              )}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function todayIso(): string {
  const d = new Date();
  const y = d.getUTCFullYear().toString().padStart(4, '0');
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}
